import { Button, IconButton, Modal } from '@braskai/ui-kit';

import { alpha, css, styled } from '@mui/material';

export const UPSELL_MOBILE_QUERY = '(max-width: 720px)';
const MOBILE = `@media ${UPSELL_MOBILE_QUERY}`;

export const HERO_GRADIENT = `
  radial-gradient(70% 90% at 12% 0%, #E7E7FC 0%, rgba(231, 231, 252, 0) 60%),
  radial-gradient(60% 80% at 100% 100%, oklch(0.74 0.13 300) 0%, rgba(180, 140, 240, 0) 70%),
  linear-gradient(135deg, #B4B4F7 0%, #8F8FF3 50%, oklch(0.62 0.17 285) 100%)
`;

export const SDialog = styled(Modal, { shouldForwardProp: (prop) => prop !== '$wide' })<{ $wide?: boolean }>(
  ({ $wide, theme }) => css`
    z-index: ${theme.zIndex.modal + 1};

    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.5)};
    }

    .MuiDialog-paper {
      position: relative;
      width: calc(100% - 32px);
      max-width: ${$wide ? 800 : 700}px;
      margin: 16px;
      overflow: hidden;
      border-radius: 24px;
      background-color: ${theme.palette.common.white};
      box-shadow: 0 32px 64px -12px ${alpha(theme.palette.grey[900], 0.35)};
      font-family: ${theme.typography.fontFamily};
    }
  `,
);

export const SScroll = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

export const SCloseButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== '$wide' })<{ $wide?: boolean }>(
  ({ $wide, theme }) => css`
    position: absolute;
    top: ${$wide ? 22.4 : 14.5}px;
    right: ${$wide ? 22.4 : 14.5}px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${$wide ? 28.8 : 32.5}px;
    height: ${$wide ? 28.8 : 32.5}px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: ${alpha(theme.palette.common.white, 0.75)};
    color: ${theme.palette.grey[600]};
    cursor: pointer;

    &:hover {
      background-color: ${theme.palette.common.white};
    }

    svg {
      width: ${$wide ? 16 : 18}px;
      height: ${$wide ? 16 : 18}px;
    }
  `,
);

export const SWelcomeGrid = styled('div')`
  display: grid;
  flex-shrink: 0;
  grid-template-columns: 55fr 45fr;
  min-height: 496px;

  ${MOBILE} {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

export const SWelcomeCopy = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 38.4px 38.4px 35.2px 41.6px;

  ${MOBILE} {
    padding: 28px 24px 24px;
  }
`;

export const SWelcomeMedia = styled('div')`
  position: relative;
  min-height: 100%;
  background: ${HERO_GRADIENT};

  ${MOBILE} {
    display: none;
  }
`;

const mediaFill = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const SVideo = styled('video')(mediaFill);

export const SVideoPoster = styled('img')(mediaFill);

export const STitle = styled('h2')(
  ({ theme }) => css`
    margin: 0;
    color: ${theme.palette.grey[900]};
    font-size: 27px;
    font-weight: 700;
    line-height: 34px;
    letter-spacing: -0.01em;
    text-wrap: pretty;

    ${MOBILE} {
      font-size: 24px;
      line-height: 30px;
    }
  `,
);

export const SCaption = styled('p')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 9px;
    color: ${theme.palette.grey[500]};
    font-size: 13.5px;
    font-weight: 500;
    line-height: 20px;

    svg {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
    }
  `,
);

export const SLabel = styled('p')(
  ({ theme }) => css`
    margin: 22.4px 0 0;
    color: ${theme.palette.grey[500]};
    font-size: 11.2px;
    font-weight: 500;
    line-height: 16px;
  `,
);

export type RowsVariant = 'download' | 'extras' | 'usage' | 'welcome';

const ROWS: Record<
  RowsVariant,
  {
    align: 'center' | 'flex-start';
    fontSize: number;
    gap: number;
    iconGap: number;
    iconSize: number;
    iconTop: number;
    lineHeight: number;
    marginTop: number;
  }
> = {
  download: {
    align: 'center',
    fontSize: 15.5,
    gap: 14.5,
    iconGap: 12.5,
    iconSize: 21.5,
    iconTop: 0,
    lineHeight: 23.5,
    marginTop: 21.5,
  },
  extras: {
    align: 'flex-start',
    fontSize: 12.8,
    gap: 9.6,
    iconGap: 9.6,
    iconSize: 17.6,
    iconTop: 0.8,
    lineHeight: 19.2,
    marginTop: 11.2,
  },
  usage: {
    align: 'flex-start',
    fontSize: 14.5,
    gap: 14.5,
    iconGap: 12.5,
    iconSize: 21.5,
    iconTop: 1,
    lineHeight: 23.5,
    marginTop: 21.5,
  },
  welcome: {
    align: 'flex-start',
    fontSize: 12.8,
    gap: 11.2,
    iconGap: 9.6,
    iconSize: 17.6,
    iconTop: 0.8,
    lineHeight: 19.2,
    marginTop: 11.2,
  },
};

export const SRows = styled('ul', { shouldForwardProp: (prop) => prop !== '$variant' })<{ $variant: RowsVariant }>(
  ({ $variant }) => {
    const { align, fontSize, gap, iconGap, iconSize, iconTop, lineHeight, marginTop } = ROWS[$variant];

    return css`
      display: flex;
      flex-direction: column;
      gap: ${gap}px;
      margin: ${marginTop}px 0 0;
      padding: 0;
      list-style: none;

      > li {
        align-items: ${align};
        gap: ${iconGap}px;
        font-size: ${fontSize}px;
        line-height: ${lineHeight}px;
      }

      > li > svg {
        width: ${iconSize}px;
        height: ${iconSize}px;
        margin-top: ${iconTop}px;
      }
    `;
  },
);

export const SRow = styled('li', { shouldForwardProp: (prop) => prop !== '$strong' })<{ $strong?: boolean }>(
  ({ $strong, theme }) => css`
    display: flex;
    color: ${theme.palette.grey[$strong ? 900 : 800]};
    font-weight: ${$strong ? 500 : 400};
    text-wrap: pretty;

    svg {
      flex-shrink: 0;
      color: ${$strong ? theme.palette.primary[600] : theme.palette.grey[600]};
    }
  `,
);

export const SDivider = styled('hr')(
  ({ theme }) => css`
    width: 100%;
    margin: 22.4px 0 19.2px;
    border: none;
    border-top: 1px solid ${theme.palette.grey[200]};
  `,
);

export const SOfferTitle = styled('p')(
  ({ theme }) => css`
    margin: 0;
    color: ${theme.palette.grey[900]};
    font-size: 12.8px;
    font-weight: 600;
    line-height: 19.2px;
  `,
);

export const SOfferText = styled('p')(
  ({ theme }) => css`
    margin: 3.2px 0 0;
    color: ${theme.palette.grey[600]};
    font-size: 12.8px;
    line-height: 19.2px;
    text-wrap: pretty;
  `,
);

export const SActions = styled('div', { shouldForwardProp: (prop) => !String(prop).startsWith('$') })<{
  $marginTop: number;
  $stack?: boolean;
}>(
  ({ $marginTop, $stack }) => css`
    display: flex;
    flex-direction: ${$stack ? 'column' : 'row'};
    flex-wrap: wrap;
    align-items: ${$stack ? 'stretch' : 'center'};
    gap: ${$stack ? 7 : 9.6}px;
    margin-top: ${$marginTop}px;
  `,
);

type ButtonScale = 'offer' | 'welcome';

const BUTTON: Record<ButtonScale, { fontSize: number; height: number; lineHeight: number; padding: number }> = {
  offer: { fontSize: 14.5, height: 43, lineHeight: 20, padding: 16 },
  welcome: { fontSize: 12.8, height: 38.4, lineHeight: 17.6, padding: 17.6 },
};

const buttonSize = (scale: ButtonScale, ghost = false) => {
  const { fontSize, height, lineHeight, padding } = BUTTON[scale];

  return css`
    min-height: ${ghost && scale === 'offer' ? 39.5 : height}px;
    padding: 4px ${ghost && scale === 'welcome' ? 14.4 : padding}px;
    font-size: ${fontSize}px;
    line-height: ${lineHeight}px;
    white-space: normal;
  `;
};

export const SPrimaryButton = styled(Button, { shouldForwardProp: (prop) => prop !== '$scale' })<{
  $scale: ButtonScale;
}>(({ $scale }) => buttonSize($scale));

export const SGhostButton = styled(Button, { shouldForwardProp: (prop) => prop !== '$scale' })<{
  $scale: ButtonScale;
}>(({ $scale }) => buttonSize($scale, true));

export const SSecondaryRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 39.5px;
  margin-top: 11px;
`;

export const SSecondaryLink = styled(Button)`
  min-height: 0;
  padding: 0 8px;
  font-size: 13.5px;
  line-height: 20px;
`;

export const HERO_WIDTH = 700;
const HERO_HEIGHT = 287;

export const SHero = styled('div')`
  position: relative;
  flex-shrink: 0;
  height: calc(${HERO_HEIGHT}px * var(--hero-scale, 1));
  overflow: hidden;
  background: ${HERO_GRADIENT};
`;

export const SHeroScale = styled('div')`
  position: absolute;
  top: 0;
  left: 50%;
  width: ${HERO_WIDTH}px;
  height: ${HERO_HEIGHT}px;
  transform: translateX(-50%) scale(var(--hero-scale, 1));
  transform-origin: top center;
`;

export const SBody = styled('div', { shouldForwardProp: (prop) => prop !== '$flow' })<{ $flow: 'download' | 'usage' }>(
  ({ $flow }) => css`
    display: flex;
    flex-direction: column;
    padding: 32.5px 43px ${$flow === 'download' ? 25 : 21.5}px;

    ${MOBILE} {
      padding: 24px 20px 16px;
    }
  `,
);
